import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle2, ArrowRight, Mail, Lock } from 'lucide-react'
import { useLogin } from '../../modules/auth/hooks/useAuth'
import { ROUTES } from '../../router/routes'

const schema = z.object({ email: z.string().email('Email invalide'), password: z.string().min(1, 'Mot de passe requis') })
type F = z.infer<typeof schema>

export default function LoginPage() {
  const [showPw, setShowPw] = useState(false)
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const { state } = useLocation() as any
  const { mutate: login, isPending, error } = useLogin()
  const { register, handleSubmit, formState: { errors } } = useForm<F>({ resolver: zodResolver(schema) })

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{
          fontFamily: '"Raleway", sans-serif', fontWeight: 800, fontSize: 26,
          color: 'var(--tx)', letterSpacing: '-0.035em', margin: '0 0 8px',
          lineHeight: 1.1,
        }}>
          Bon retour<span style={{ color: 'var(--brand-orange)' }}>.</span>
        </h2>
        <p style={{ fontSize: 14, color: 'var(--tx3)', lineHeight: 1.5 }}>
          Connectez-vous à votre espace NexusCorp
        </p>
      </div>

      {/* Toasts */}
      {state?.passwordReset && <Toast type="success" msg="Mot de passe réinitialisé. Reconnectez-vous." />}
      {state?.accountActivated && <Toast type="success" msg="Compte activé. Bienvenue !" />}
      {error && <Toast type="error" msg={(error as any)?.response?.data?.message ?? 'Identifiants incorrects'} />}

      <form onSubmit={handleSubmit(d => login(d))} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Email */}
        <div>
          <label style={{
            display: 'block', fontSize: 12, fontWeight: 600,
            color: 'var(--tx2)', marginBottom: 8, fontFamily: '"Raleway", sans-serif',
          }}>
            Adresse email
          </label>
          <div style={{
            position: 'relative', display: 'flex', alignItems: 'center',
            borderRadius: 12, overflow: 'hidden',
            border: `1.5px solid ${errors.email ? '#EF4444' : focusedField === 'email' ? 'var(--brand-orange)' : 'var(--bd)'}`,
            boxShadow: focusedField === 'email' ? '0 0 0 3px rgba(245,124,0,0.10)' : 'none',
            background: focusedField === 'email' ? '#fff' : 'var(--surface-2)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}>
            <div style={{
              width: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: focusedField === 'email' ? 'var(--brand-orange)' : 'var(--tx4)',
              transition: 'color 0.2s',
              flexShrink: 0,
            }}>
              <Mail size={16} />
            </div>
            <input
              {...register('email')}
              type="email"
              autoComplete="email"
              placeholder="vous@nexuscorp.sn"
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              style={{
                width: '100%', height: 48, padding: '0 14px 0 0',
                border: 'none', outline: 'none', fontSize: 14,
                color: 'var(--tx)', background: 'transparent',
                fontFamily: '"Inter", sans-serif',
              }}
            />
          </div>
          {errors.email && (
            <p style={{ fontSize: 12, color: 'var(--red)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <AlertCircle size={12} />{errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{
              fontSize: 12, fontWeight: 600, color: 'var(--tx2)', fontFamily: '"Raleway", sans-serif',
            }}>
              Mot de passe
            </label>
            <Link to={ROUTES.FORGOT_PASSWORD} style={{
              fontSize: 12, color: 'var(--brand-orange)', textDecoration: 'none',
              fontWeight: 500, transition: 'opacity 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Oublié ?
            </Link>
          </div>
          <div style={{
            position: 'relative', display: 'flex', alignItems: 'center',
            borderRadius: 12, overflow: 'hidden',
            border: `1.5px solid ${errors.password ? '#EF4444' : focusedField === 'password' ? 'var(--brand-orange)' : 'var(--bd)'}`,
            boxShadow: focusedField === 'password' ? '0 0 0 3px rgba(245,124,0,0.10)' : 'none',
            background: focusedField === 'password' ? '#fff' : 'var(--surface-2)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}>
            <div style={{
              width: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: focusedField === 'password' ? 'var(--brand-orange)' : 'var(--tx4)',
              transition: 'color 0.2s',
              flexShrink: 0,
            }}>
              <Lock size={16} />
            </div>
            <input
              {...register('password')}
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
              style={{
                width: '100%', height: 48, padding: '0 44px 0 0',
                border: 'none', outline: 'none', fontSize: 14,
                color: 'var(--tx)', background: 'transparent',
                fontFamily: '"Inter", sans-serif',
              }}
            />
            <button
              type="button"
              onClick={() => setShowPw(v => !v)}
              style={{
                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: 'var(--tx4)', padding: 4, borderRadius: 6,
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--tx2)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--tx4)'}
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p style={{ fontSize: 12, color: 'var(--red)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <AlertCircle size={12} />{errors.password.message}
            </p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isPending}
          className="login-submit-btn"
          style={{
            width: '100%', height: 50, borderRadius: 12, border: 'none',
            cursor: isPending ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: 14.5, color: '#fff',
            background: isPending ? 'rgba(245,124,0,0.5)' : 'linear-gradient(135deg, var(--brand-orange) 0%, var(--brand-amber) 100%)',
            boxShadow: isPending ? 'none' : '0 4px 20px rgba(245,124,0,0.30), inset 0 1px 0 rgba(255,255,255,0.15)',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            marginTop: 6,
            letterSpacing: '-0.01em',
          }}
        >
          {isPending ? (
            <><Loader2 size={17} className="animate-spin" />Connexion…</>
          ) : (
            <>Se connecter <ArrowRight size={16} style={{ transition: 'transform 0.2s' }} /></>
          )}
        </button>
      </form>
    </div>
  )
}

function Toast({ type, msg }: { type: 'success' | 'error'; msg: string }) {
  const ok = type === 'success'
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, borderRadius: 12,
      padding: '12px 16px', marginBottom: 20, fontSize: 13,
      background: ok ? '#ECFDF5' : '#FEF2F2',
      border: `1px solid ${ok ? '#A7F3D0' : '#FECACA'}`,
      color: ok ? '#065F46' : '#991B1B',
      animation: 'nx-fade-up 0.3s ease',
    }}>
      {ok ? <CheckCircle2 size={16} style={{ flexShrink: 0 }} /> : <AlertCircle size={16} style={{ flexShrink: 0 }} />}
      <span style={{ fontWeight: 500 }}>{msg}</span>
    </div>
  )
}
